/**
 * Pure validation helpers for flexible/custom payment schedules.
 * Exported as plain functions so they can be unit-tested without any React setup.
 */

import type { PaymentScheduleItem } from "@/components/contracts/wizard-v2/types";

/** Tolerance for floating-point sum comparisons (0.01 SAR). */
const AMOUNT_TOLERANCE = 0.01;

/**
 * Validates that every payment's due date falls within [startDate, endDate].
 *
 * @returns Array of 0-based indices that are out of range.
 */
export function validatePaymentDatesInRange(
  payments: PaymentScheduleItem[],
  startDate: string,
  endDate: string
): number[] {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const outOfRange: number[] = [];
  payments.forEach((p, i) => {
    if (!p.dueDate) {
      outOfRange.push(i);
      return;
    }
    const d = new Date(p.dueDate);
    if (d < start || d > end) {
      outOfRange.push(i);
    }
  });
  return outOfRange;
}

/**
 * Validates that sum(payments[].amount) === targetTotal within tolerance.
 *
 * @returns The difference (sum - target). 0 ± tolerance = pass.
 */
export function validatePaymentSumEqualsTotal(
  payments: PaymentScheduleItem[],
  targetTotal: number
): number {
  const sum = payments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  return Math.round((sum - targetTotal) * 100) / 100;
}

/**
 * Returns true if the custom schedule passes both validations.
 */
export function isCustomScheduleValid(
  payments: PaymentScheduleItem[],
  startDate: string,
  endDate: string,
  targetTotal: number
): boolean {
  if (payments.length === 0) return false;
  if (validatePaymentDatesInRange(payments, startDate, endDate).length > 0) return false;
  const diff = validatePaymentSumEqualsTotal(payments, targetTotal);
  return Math.abs(diff) <= AMOUNT_TOLERANCE;
}
